/**
 * Team Store
 *
 * Zustand store for managing team members, invites, and seat usage.
 * Uses Supabase Edge Functions for secure invite handling.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';

import {
  Team,
  TeamMember,
  TeamInvite,
  TeamRole,
  TeamMemberStatus,
  generateInviteToken,
  isInviteExpired,
  INVITE_EXPIRY_DAYS,
  ADDITIONAL_SEATS_BY_TIER,
  getTotalSeats,
  canRemoveRole,
} from './types';
import { mode } from '@/lib/config/app-mode';
import {
  sendTeamInvite as sendTeamInviteEdge,
  acceptTeamInvite as acceptTeamInviteEdge,
} from '../supabase/edge-functions';

// ============================================================================
// State Interface
// ============================================================================

interface TeamState {
  // Current team data
  team: Team | null;

  // Current user's role
  myRole: TeamRole | null;

  // Loading states
  isLoading: boolean;

  // Actions
  initializeTeam: (ownerId: string, ownerName: string, ownerEmail: string, teamName: string) => void;
  inviteMember: (email: string, role: TeamRole, invitedBy: string, founderStatus?: 'co_founder' | 'team_member') => Promise<{ success: boolean; error?: string; invite?: TeamInvite }>;
  acceptInvite: (token: string, userId: string, userName: string) => Promise<{ success: boolean; error?: string }>;
  removeMember: (memberId: string, actorRole: TeamRole) => Promise<{ success: boolean; error?: string }>;
  revokeInvite: (inviteId: string) => void;
  updateMemberRole: (memberId: string, newRole: TeamRole, actorRole: TeamRole) => Promise<{ success: boolean; error?: string }>;
  getUsedSeats: () => number;
  getAvailableSeats: (tier: keyof typeof ADDITIONAL_SEATS_BY_TIER) => number;
  canInvite: (tier: keyof typeof ADDITIONAL_SEATS_BY_TIER) => boolean;
  getActiveMembers: () => TeamMember[];
  getPendingInvites: () => TeamInvite[];
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  team: null,
  myRole: null,
  isLoading: false,
};

// ============================================================================
// Store
// ============================================================================

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Initialize a new team for the owner
       */
      initializeTeam: (ownerId, ownerName, ownerEmail, teamName) => {
        const existingTeam = get().team;
        if (existingTeam && existingTeam.ownerId === ownerId) {
          return; // Team already exists for this owner
        }

        const owner: TeamMember = {
          id: ownerId,
          email: ownerEmail,
          name: ownerName,
          role: 'owner',
          status: 'active',
          invitedAt: new Date().toISOString(),
          joinedAt: new Date().toISOString(),
          invitedBy: ownerId,
          lastActiveAt: new Date().toISOString(),
        };

        const team: Team = {
          id: `team_${Date.now()}`,
          ownerId,
          name: teamName,
          createdAt: new Date().toISOString(),
          members: [owner],
          pendingInvites: [],
        };

        set({ team, myRole: 'owner' });
      },

      /**
       * Invite a new team member
       */
      inviteMember: async (email, role, invitedBy, founderStatus) => {
        const { team } = get();
        if (!team) {
          return { success: false, error: 'No team found' };
        }

        // Check if email is already a member
        const existingMember = team.members.find(
          (m) => m.email.toLowerCase() === email.toLowerCase() && m.status === 'active'
        );
        if (existingMember) {
          return { success: false, error: 'This person is already a team member' };
        }

        // Check for existing pending invite
        const existingInvite = team.pendingInvites.find(
          (i) => i.email.toLowerCase() === email.toLowerCase() && i.status === 'pending'
        );
        if (existingInvite && !isInviteExpired(existingInvite)) {
          return { success: false, error: 'An invite has already been sent to this email' };
        }

        // Use Edge Function when not in mock mode
        if (!mode.isMock) {
          const edgeRole = role === 'owner' ? 'admin' : role as 'admin' | 'member';
          const { data, error } = await sendTeamInviteEdge({
            email: email.toLowerCase(),
            role: edgeRole,
            // `inviteMember`'s founderStatus param stays optional (existing
            // public API), but the edge function's request now requires it
            // (final-review MINOR — matches the server schema) — default to
            // the conservative non-owner status rather than omitting it.
            founderStatus: founderStatus ?? 'team_member',
          });

          if (error || !data) {
            return { success: false, error: error || 'Failed to send invite' };
          }

          // Create local invite with data from edge function
          const invite: TeamInvite = {
            id: data.inviteId,
            email: email.toLowerCase(),
            role,
            invitedBy,
            invitedAt: new Date().toISOString(),
            expiresAt: data.expiresAt,
            token: data.token,
            status: 'pending',
          };

          // Update local state
          const filteredInvites = team.pendingInvites.filter(
            (i) => i.email.toLowerCase() !== email.toLowerCase() || i.status === 'accepted'
          );

          set({
            team: {
              ...team,
              pendingInvites: [...filteredInvites, invite],
            },
          });

          return { success: true, invite };
        }

        // Mock implementation for mock mode
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + INVITE_EXPIRY_DAYS);

        const invite: TeamInvite = {
          id: `invite_${Date.now()}`,
          email: email.toLowerCase(),
          role,
          invitedBy,
          invitedAt: new Date().toISOString(),
          expiresAt: expiresAt.toISOString(),
          token: generateInviteToken(),
          status: 'pending',
        };

        // Remove any expired/revoked invites for same email
        const filteredInvites = team.pendingInvites.filter(
          (i) => i.email.toLowerCase() !== email.toLowerCase() || i.status === 'accepted'
        );

        set({
          team: {
            ...team,
            pendingInvites: [...filteredInvites, invite],
          },
        });

        return { success: true, invite };
      },

      /**
       * Accept a team invite
       */
      acceptInvite: async (token, userId, userName) => {
        // Use Edge Function when not in mock mode
        if (!mode.isMock) {
          const { data, error } = await acceptTeamInviteEdge({ token });

          if (error || !data) {
            return { success: false, error: error || 'Failed to accept invite' };
          }

          // Update local state - team will be synced from server
          // For now, create a placeholder team
          const { team } = get();
          const invite = team?.pendingInvites.find((i) => i.token === token);

          if (invite) {
            const newMember: TeamMember = {
              id: userId,
              email: invite.email,
              name: userName,
              role: invite.role,
              status: 'active',
              invitedAt: invite.invitedAt,
              joinedAt: new Date().toISOString(),
              invitedBy: invite.invitedBy,
              lastActiveAt: new Date().toISOString(),
            };

            set({
              team: team ? {
                ...team,
                id: data.teamId,
                name: data.teamName,
                members: [...team.members, newMember],
                pendingInvites: team.pendingInvites.map((i) =>
                  i.token === token ? { ...i, status: 'accepted' as const } : i
                ),
              } : {
                id: data.teamId,
                ownerId: '', // Will be synced from server
                name: data.teamName,
                createdAt: new Date().toISOString(),
                members: [newMember],
                pendingInvites: [],
              },
              myRole: invite?.role || 'member',
            });
          }

          return { success: true };
        }

        // Mock implementation for mock mode
        const { team } = get();
        if (!team) {
          return { success: false, error: 'No team found' };
        }

        // Find the invite
        const invite = team.pendingInvites.find(
          (i) => i.token === token && i.status === 'pending'
        );
        if (!invite) {
          return { success: false, error: 'Invalid or expired invite' };
        }

        // Check if expired
        if (isInviteExpired(invite)) {
          // Mark as expired
          set({
            team: {
              ...team,
              pendingInvites: team.pendingInvites.map((i) =>
                i.id === invite.id ? { ...i, status: 'expired' as const } : i
              ),
            },
          });
          return { success: false, error: 'This invite has expired' };
        }

        // Create new member
        const newMember: TeamMember = {
          id: userId,
          email: invite.email,
          name: userName,
          role: invite.role,
          status: 'active',
          invitedAt: invite.invitedAt,
          joinedAt: new Date().toISOString(),
          invitedBy: invite.invitedBy,
          lastActiveAt: new Date().toISOString(),
        };

        // Update team
        set({
          team: {
            ...team,
            members: [...team.members, newMember],
            pendingInvites: team.pendingInvites.map((i) =>
              i.id === invite.id ? { ...i, status: 'accepted' as const } : i
            ),
          },
          myRole: invite.role,
        });

        return { success: true };
      },

      /**
       * Remove a team member
       */
      removeMember: async (memberId, actorRole) => {
        const { team } = get();
        if (!team) {
          return { success: false, error: 'No team found' };
        }

        const member = team.members.find((m) => m.id === memberId);
        if (!member) {
          return { success: false, error: 'Member not found' };
        }

        // Check permissions
        if (!canRemoveRole(actorRole, member.role)) {
          return { success: false, error: 'You do not have permission to remove this member' };
        }

        // Deactivate member (soft delete)
        set({
          team: {
            ...team,
            members: team.members.map((m) =>
              m.id === memberId ? { ...m, status: 'deactivated' as TeamMemberStatus } : m
            ),
          },
        });

        return { success: true };
      },

      /**
       * Revoke a pending invite
       */
      revokeInvite: (inviteId) => {
        const { team } = get();
        if (!team) return;

        set({
          team: {
            ...team,
            pendingInvites: team.pendingInvites.map((i) =>
              i.id === inviteId ? { ...i, status: 'revoked' as const } : i
            ),
          },
        });
      },

      /**
       * Update a member's role
       */
      updateMemberRole: async (memberId, newRole, actorRole) => {
        const { team } = get();
        if (!team) {
          return { success: false, error: 'No team found' };
        }

        // Only owner can change roles
        if (actorRole !== 'owner') {
          return { success: false, error: 'Only the owner can change member roles' };
        }

        // Cannot change owner role
        const member = team.members.find((m) => m.id === memberId);
        if (member?.role === 'owner') {
          return { success: false, error: 'Cannot change owner role' };
        }

        set({
          team: {
            ...team,
            members: team.members.map((m) =>
              m.id === memberId ? { ...m, role: newRole } : m
            ),
          },
        });

        return { success: true };
      },

      /**
       * Get the number of used seats (active members + pending invites)
       */
      getUsedSeats: () => {
        const { team } = get();
        if (!team) return 0;

        const activeMembers = team.members.filter((m) => m.status === 'active').length;
        const pendingInvites = team.pendingInvites.filter(
          (i) => i.status === 'pending' && !isInviteExpired(i)
        ).length;

        return activeMembers + pendingInvites;
      },

      /**
       * Get the number of available seats
       */
      getAvailableSeats: (tier) => {
        // B10: single source of truth is features.ts team_seats (via getTotalSeats).
        const totalSeats = getTotalSeats(tier);
        const usedSeats = get().getUsedSeats();
        return Math.max(0, totalSeats - usedSeats);
      },

      /**
       * Check if team can invite more members
       */
      canInvite: (tier) => {
        return get().getAvailableSeats(tier) > 0;
      },

      /**
       * Get all active members
       */
      getActiveMembers: () => {
        const { team } = get();
        if (!team) return [];
        return team.members.filter((m) => m.status === 'active');
      },

      /**
       * Get all pending (non-expired) invites
       */
      getPendingInvites: () => {
        const { team } = get();
        if (!team) return [];
        return team.pendingInvites.filter(
          (i) => i.status === 'pending' && !isInviteExpired(i)
        );
      },

      /**
       * Reset team state
       */
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'inverio-team',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        team: state.team,
        myRole: state.myRole,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const useTeam = () => useTeamStore((s) => s.team);
export const useMyRole = () => useTeamStore((s) => s.myRole);
export const useActiveMembers = () => useTeamStore((s) => s.getActiveMembers());
export const usePendingInvites = () => useTeamStore((s) => s.getPendingInvites());
export const useUsedSeats = () => useTeamStore((s) => s.getUsedSeats());
